/**
 * Preview table + discrepancy details for the Order Acknowledgements import
 * (CR-6679). Moved out of OrderAckImportModal unchanged (Customer review 11 Sep,
 * drag and drop everywhere) to keep that file under the 500-line limit.
 */
import { AlertCircle, AlertTriangle, CheckCircle, MinusCircle } from 'lucide-react';
import type { AckImportPreviewRow } from '../../lib/api/types/po-types';

interface OrderAckPreviewTableProps {
  previewData: AckImportPreviewRow[];
}

export default function OrderAckPreviewTable({ previewData }: OrderAckPreviewTableProps) {
  const discrepancyRowCount = previewData.filter(row => row.status === 'discrepancy').length;
  return (
    <>
      {/* Preview Table */}
      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left py-2 px-3 font-medium">Row</th>
              <th className="text-left py-2 px-3 font-medium">Status</th>
              <th className="text-left py-2 px-3 font-medium">PO #</th>
              <th className="text-left py-2 px-3 font-medium">Line</th>
              <th className="text-left py-2 px-3 font-medium">Part #</th>
              <th className="text-right py-2 px-3 font-medium">Ack Qty</th>
              <th className="text-right py-2 px-3 font-medium">Ack Price</th>
              <th className="text-left py-2 px-3 font-medium">Ack Date</th>
            </tr>
          </thead>
          <tbody>
            {previewData.slice(0, 50).map((row, idx) => (
              <tr
                key={idx}
                className={`border-b ${
                  row.status === 'error' ? 'bg-red-50' :
                  row.status === 'discrepancy' ? 'bg-amber-50' :
                  row.status === 'unchanged' ? 'bg-muted/40' : ''
                }`}
              >
                <td className="py-2 px-3">{row.row}</td>
                <td className="py-2 px-3">
                  {row.status === 'error' && (
                    <span className="text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Error
                    </span>
                  )}
                  {row.status === 'discrepancy' && (
                    <span className="text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Discrepancy
                    </span>
                  )}
                  {row.status === 'unchanged' && (
                    <span className="text-muted-foreground flex items-center gap-1" title={row.errors.join('; ')}>
                      <MinusCircle className="h-3 w-3" />
                      No change
                    </span>
                  )}
                  {row.status === 'valid' && (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Valid
                    </span>
                  )}
                </td>
                <td className="py-2 px-3 font-mono">{row.po_number}</td>
                <td className="py-2 px-3">{row.line_number}</td>
                <td className="py-2 px-3 font-mono">{row.part_number}</td>
                <td className="py-2 px-3 text-right">{row.ack_qty || '-'}</td>
                <td className="py-2 px-3 text-right">{row.ack_unit_price || '-'}</td>
                <td className="py-2 px-3">{row.ack_date || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {previewData.length > 50 && (
          <div className="text-center py-2 text-sm text-muted-foreground border-t">
            Showing first 50 of {previewData.length} rows
          </div>
        )}
      </div>

      {/* Discrepancy Details */}
      {discrepancyRowCount > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="px-4 py-2 bg-amber-50 border-b font-medium text-sm text-amber-800">
            Discrepancy Details
          </div>
          <div className="max-h-48 overflow-y-auto">
            {previewData
              .filter(row => row.status === 'discrepancy')
              .slice(0, 20)
              .map((row, idx) => (
                <div key={idx} className="px-4 py-2 border-b last:border-0 text-sm">
                  <div className="font-medium">
                    Row {row.row} — PO {row.po_number} Line {row.line_number}
                  </div>
                  <ul className="mt-1 space-y-1">
                    {row.discrepancies.map((disc, dIdx) => (
                      <li key={dIdx} className="text-amber-700 text-xs">
                        <span className="font-medium capitalize">{disc.field.replace('_', ' ')}:</span>{' '}
                        PO value "{disc.po_value}" vs Supplier value "{disc.supplier_value}"
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
}
